import apiService from './api.service';
import {
  UploadResponse,
  ValidationSummary,
  ImportProgress,
  ImportJob,
  ImportedLead,
  FieldMapping,
  ImportOptions,
} from '../types/import.types';

export const importService = {
  /**
   * Upload file and create import job
   */
  async uploadFile(file: File): Promise<UploadResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiService.post('/leads/import/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data.data;
  },

  /**
   * Validate and stage import
   */
  async validateImport(
    jobId: string,
    fieldMapping: FieldMapping,
    options: ImportOptions
  ): Promise<ValidationSummary> {
    const response = await apiService.post('/leads/import/validate', {
      jobId,
      fieldMapping,
      options,
    });

    return response.data.data;
  },

  /**
   * Get import records with pagination
   */
  async getImportRecords(
    jobId: string,
    status: string = 'all',
    page: number = 1,
    limit: number = 50
  ): Promise<{
    records: ImportedLead[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      pages: number;
    };
  }> {
    const response = await apiService.get(
      `/leads/import/${jobId}/records`,
      {
        params: { status, page, limit },
      }
    );

    return response.data.data;
  },

  /**
   * Update import record
   */
  async updateImportRecord(
    recordId: string,
    updates: Partial<ImportedLead>
  ): Promise<ImportedLead> {
    const response = await apiService.patch(
      `/leads/import/records/${recordId}`,
      updates
    );

    return response.data.data;
  },

  /**
   * Approve and start import
   */
  async approveImport(
    jobId: string,
    duplicateHandling: 'skip' | 'update' | 'create',
    includeWarnings: boolean = true
  ): Promise<void> {
    await apiService.post(`/leads/import/${jobId}/approve`, {
      duplicateHandling,
      importOptions: {
        includeWarnings,
        skipErrors: true,
      },
    });
  },

  /**
   * Get import status
   */
  async getImportStatus(jobId: string): Promise<ImportProgress> {
    const response = await apiService.get(`/leads/import/${jobId}/status`);
    return response.data.data;
  },

  /**
   * Cancel import
   */
  async cancelImport(jobId: string): Promise<void> {
    await apiService.post(`/leads/import/${jobId}/cancel`);
  },

  /**
   * Get import history
   */
  async getImportHistory(
    page: number = 1,
    limit: number = 20
  ): Promise<{ jobs: ImportJob[]; total: number }> {
    const response = await apiService.get('/api/leads/import/history', {
      params: { page, limit },
    });

    return response.data.data;
  },
};